import { CloseOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Modal,
  Row,
  Select,
  Spin,
  Table,
  Tabs,
  message,
} from "antd";
import { CALL_API } from "common/API";
import COLORS from "common/Colors";
import { LEAVE_ICON, NON_EMPTY_REGEX, STATUS_CODE } from "common/Constants";
import { concat, includes, orderBy, reduce } from "lodash";
import AllStaffLeaveBalance from "molecules/AllStaffLeaveBalance";
import AttendanceBarChart from "molecules/AttendanceBarChart";
import LeaveDonutChart from "molecules/LeaveDonutChart";
import LocationBasedPresentStaff from "molecules/LocationBasedPresentStaff";
import moment from "moment";
import LeaveFormModel from "organisms/LeaveFormModel";
import { useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { getMySqlDate } from "utils/Date";
import Filter from "../assets/Filter.svg";
import { CheckCircleFilled } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import download from "downloadjs";
import ViewDocument from "reusableComponent/Popup/viewDocument";
import { useSelector } from "react-redux";
import { getUserList } from "redux/selectors";
import { ButtonFilled } from "reusableComponent/UIButtons";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";
import TextArea from "antd/lib/input/TextArea";
import { useTranslation } from "react-i18next";

const ModalWrap = styled(Modal)`
  .ant-modal-content {
    border-radius: 14px;
    padding: 10px 8px 0px 8px;
  }
  .ant-modal-header {
    border-bottom: none;
    padding-bottom: 0;
  }
  .ant-modal-title {
    font-size: 20px;
    font-weight: 600;
    margin-top: -12px;
  }
  .ant-modal-body {
    padding: 10px 20px 24px;
  }
  .top-icons {
    position: absolute;
    right: 55px;
    top: 18px;
    display: flex;
    gap: 18px;
    font-size: 18px;
    color: #5b6fd6;
    cursor: pointer;
  }
  .close-icon {
    position: absolute;
    right: 20px;
    top: 18px;
    font-size: 18px;
    cursor: pointer;
    color: #666;
  }
  .applied-tag {
    background: #e9ecfb;
    border-radius: 20px;
    padding: 3px 14px;
    font-size: 13px;
    margin-bottom: 18px;
    display: inline-block;
  }
  .label {
    color: #8b8b8b;
    font-weight: 500;
  }
  .value {
    color: #1f1f1f;
    font-weight: 500;
  }
  .attachment {
    color: #2d53da;
    cursor: pointer;
  }
  .reason-title {
    margin-top: 16px;
    color: #8b8b8b;
    font-weight: 500;
  }
  .reason-text {
    margin-top: 6px;
    color: #1f1f1f;
    line-height: 1.6;
  }
  @media (max-width: 768px) {
    .value {
      text-align: left;
      margin-top: 4px;
    }
    .top-icons {
      right: 45px;
      gap: 12px;
    }
    .ant-modal-body {
      padding: 10px 14px 22px;
    }
  }
`;

const StyledRow = styled(Row)`
  padding-top: 20px;
`;

const ButtonOutlined = styled(Button)`
  padding: 5px 20px;
  border: 1px solid ${COLORS.PRIMARY};
  border-radius: 5px;
  background: transparent;
  font-weight: normal;
  color: ${COLORS.PRIMARY};
  white-space: nowrap;
  &:hover,
  &:focus {
    border: 1px solid ${COLORS.PRIMARY};
    background: #f1f3f4;
    color: ${COLORS.PRIMARY};
  }
  @media (max-width: 900px) {
    margin: 10px 0px;
  }
`;

const StyledLeaveRow = styled(Row)`
  padding: 20px;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 5px 15px;
  border-radius: 5px;
  height: 100%;
  margin: 20px;
  @media (max-width: 417px) {
    display: block !important;
    padding-bottom: 10px;
    .leavereportstyle {
      margin-left: 70px;
    }
  }
`;

const StyledChartContainer = styled.div`
  padding: 20px !important;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 5px 15px;
  border-radius: 5px !important;
  padding-bottom: 25px;
  margin-top: 20px;
  .chartstyle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  @media (max-width: 410px) {
    .chartstyle {
      display: block;
      align-items: left !important;
    }
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  .select {
    width: 150px;
  }
  @media (max-width: 410px) {
    margin-top: 5px;
    justify-content: flex-start;
    align-items: left !important;
  }
`;

const StyledTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #141414;
  margin-right: auto;
`;

const StyledTable = styled(Table)`
  padding-top: 20px;
  table {
    width: 100% !important;
  }
  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
    border: 1px solid #00000024;
    border-radius: 10px;
  }
  .ant-table-cell {
    padding: 20px;
    border: none !important;
  }
  .normal-column {
    min-width: 300px;
    white-space: normal;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: 600;
    font-size: 0.9rem;
    color: ${COLORS.SECONDARY_BLACK};
    white-space: nowrap;
  }
  .non-white {
    background: #f9f9f9;
  }
  .ant-table-thead > tr > th::before {
    display: none !important;
  }
  .ant-table-cell-row-hover {
    background: #e3e8fc !important;
  }
`;

function HrDashboard({
  ClientId,
  selectedDepartmentId,
  userRoleId,
  departmentList,
  roleList,
  AllDepartmentList,
  Menulist,
  leaveCategoryList,
  userList,
  updateSuccessModal,
  leaveList,
  setLeaveList,
  type,
  userInfo,
}) {
  const { t } = useTranslation();
  const users = useSelector(getUserList);

  const [leaveCatgeory, setLeaveCatgeory] = useState([]);
  const [displayTaskLoader, setDisplayTaskLoader] = useState(false);
  const [displayLeaveModal, toggleDisplayLeaveModal] = useState(false);
  const [leaveDetails, setLeaveDetails] = useState({});
  const [leaveID, setLeaveID] = useState();
  const [havingCancel, sethavingCancel] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [leaveSummary, setLeaveSummary] = useState({});
  const [displayLeaveReportLoader, toggleLeaveReportLoader] = useState(false);
  const [staffDepartmentLeaveModal, toggleStaffDepartmentLeaveModal] =
    useState(false);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState("");
  const [selectedLeaveType, setSelectedLeaveType] = useState("");
  const [userLeaves, setUserLeaves] = useState([]);
  const [CSVDepartmentLeave, setCSVDepartmentLeave] = useState([]);
  const [allLeaveConsumed, setAllLeaveConsumed] = useState();
  const [totalDepartmentLeave, setTtlDepartmentLeave] = useState();
  const [availableLeaves, setAvailableLeaves] = useState([]);
  console.log("avilabelLeaves33", availableLeaves);
  const [leavedataBasedonLeaveId, setLeaveDataBasedonLeaveId] = useState({});
  const [preview, setPreview] = useState(false);
  const [previewURL, setPreviewURL] = useState();
  const [viewData, setViewData] = useState({});
  const [open, setOpen] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("0");
  const [CSVDataList, setCSVDataList] = useState([]);
  const [filterdate, setfilterdate] = useState(dayjs());
  console.log("filterdate", filterdate);
  const [leaveData, setLeaveData] = useState([]);
  const [overtimeData, setOvertimeData] = useState([]);
  const [wfhData, setWfhData] = useState([]);
  const [timeOffData, setTimeOffData] = useState([]);

  const csvLinkDepartment = useRef();
  const csvLinkEl = useRef();
  const containerRef = useRef(null);
  const [form] = Form.useForm();
  const [updateform] = Form.useForm();

  const [tabId, setTabId] = useState(
    Menulist?.find(
      (item) =>
        item?.key === "allapprovals" &&
        item?.subMenu?.find(
          (subItem) => subItem?.key === "approvals" && subItem?.permission?.view
        )
    )
      ? "1"
      : "2"
  );

  function getDates() {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    return {
      startDate: new Date(currentYear - 2, 0, 1),
      endDate: new Date(currentYear + 1, 11, 31),
    };
  }
  const dates = getDates();

  const getLeaveList = async () => {
    if (!selectedDepartmentId) return;
    setDisplayTaskLoader(true);
    const { code, leaves = [] } = await CALL_API(
      `leave-list/${ClientId}`,
      "post",
      {
        status_id: "4",
        department_id: [""],
        start_date: dates.startDate,
        end_date: dates.endDate,
      }
    );
    setDisplayTaskLoader(false);
    if (includes([STATUS_CODE.SUCCESS, STATUS_CODE.RECORD_NOT_FOUND], code)) {
      setLeaveList(leaves.reverse());
    }
  };

const getLeaveListCategories = async () => {
    return new Promise(async (resolve, reject) => {
      setLeaveCatgeory([]);

      if (selectedDepartmentId) {
        // toggleLeaveReportLoader(true);
        const { code, leaves = [] } = await CALL_API(
          `employee/allleave`,
          "post",
          {
            clientid: ClientId,
            year: new Date().getFullYear().toString(),
            reportingmanager: "",
            userid: [],
          }
        );
        setLeaveCatgeory(leaves);

        resolve(leaves);
        // toggleLeaveReportLoader(false);
        // if (
        //   includes([STATUS_CODE.SUCCESS, STATUS_CODE.RECORD_NOT_FOUND], code)
        // ) {
        //   setLeaveCatgeory(response);

        //   resolve(response);
        // } else {
        //   reject([]);
        // }
      } else {
        reject([]);
      }
    });
  };

  const getOvertimeData = async () => {
    setLoading(true);
    const { code, data } = await CALL_API(
      `employeerequest/overtimerequest?reportingManager=&statusId=${selectedStatus}&startDate=${dayjs(
        dates.startDate
      ).format("YYYY-MM-DD")}&endDate=${dayjs(dates.endDate).format(
        "YYYY-MM-DD"
      )}`,
      "get"
    );
    if (code === STATUS_CODE.SUCCESS) setOvertimeData(data || []);
    setLoading(false);
  };

  const getWfhData = async () => {
    setLoading(true);
    const { code, data } = await CALL_API(
      `employeerequest/wfhrequest?statusId=${selectedStatus}&startDate=${dayjs(
        dates.startDate
      ).format("YYYY-MM-DD")}&endDate=${dayjs(dates.endDate).format(
        "YYYY-MM-DD"
      )}`,
      "get"
    );
    if (code === STATUS_CODE.SUCCESS) setWfhData(data || []);
    setLoading(false);
  };

  const getTimeOffData = async () => {
    setLoading(true);
    const { code, data } = await CALL_API(
      `employeerequest/permissionrequest?reportingManager=&statusId=${selectedStatus}&startDate=${dayjs(
        dates.startDate
      ).format("YYYY-MM-DD")}&endDate=${dayjs(dates.endDate).format(
        "YYYY-MM-DD"
      )}`,
      "get"
    );
    if (code === STATUS_CODE.SUCCESS) setTimeOffData(data || []);
    setLoading(false);
  };

  const getAllDepartmentTotalLeaveWise = async () => {
    toggleLeaveReportLoader(true);
    const api =
      "https://1w0c2tpfc4.execute-api.eu-west-1.amazonaws.com/Prod/leave-service/";
    const obj = {
      method: "overallTotalLeavesAvailableAndConsumed",
      parameters: {
        year: new Date().getFullYear().toString(),
        departmentId: [""],
      },
    };
    try {
      const headers = {
        Tymeplusjwt: window.localStorage.getItem("token") || "",
      };
      const response = await axios.post(`${api}${ClientId}`, obj, { headers });
      if (response.data.status === "SUCCESS") {
        setAllLeaveConsumed(response.data);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      toggleLeaveReportLoader(false);
    }
  };

  useEffect(() => {
    getAllDepartmentTotalLeaveWise();
    getLeaveList();
    getLeaveListCategories();
  }, []);

  useEffect(() => {
    if (tabId === "2") getOvertimeData();
    if (tabId === "3") getWfhData();
    if (tabId === "4") getTimeOffData();
  }, [selectedStatus, tabId]);

  useEffect(() => {
    if (!leaveCategoryList?.length || !userList?.length) return;

    let overAllAvailableLeave = {};
    let overAllConsumedLeave = {};
    let overAllCarryforwardLeave = {};
    let allActiveUserData = {};

    leaveCategoryList.forEach((category) => {
      const categoryId = category.leave_category_id;
      let availableCount = 0;
      let consumedCount = 0;
      let carryforwardCount = 0;

      leaveCatgeory?.forEach((categoryDetails) => {
        if (categoryDetails?.categoryid === categoryId) {
          userList?.forEach((user) => {
            if (
              categoryDetails?.userid === user.userid &&
              category?.locationId?.includes(user?.baselocationid)
            ) {
              if (!allActiveUserData[categoryId]) {
                allActiveUserData[categoryId] = { available: [], consumed: [] };
              }
              allActiveUserData[categoryId].available.push(categoryDetails);
            }
          });
        }
      });

      allActiveUserData[categoryId]?.available?.forEach((user) => {
        const userGender = user.gender?.toLowerCase();
        const eligibleGender = category.eligible?.toLowerCase();
        if (
          user.categoryid === categoryId &&
          (userGender === eligibleGender || eligibleGender === "both")
        ) {
          availableCount += parseFloat(user.available) || 0;
          consumedCount += parseFloat(user.consumed) || 0;
          carryforwardCount += parseFloat(user.carry_forward) || 0;
        }
      });

      setLeaveDataBasedonLeaveId(allActiveUserData);
      overAllAvailableLeave[categoryId] = availableCount;
      overAllConsumedLeave[categoryId] = consumedCount;
      overAllCarryforwardLeave[categoryId] = carryforwardCount;
    });

    const updatedLeaveData = leaveCategoryList.map((category) => ({
      categoryName: category.leave_category_name,
      id: category.leave_category_id,
      data: [
        {
          type: "available",
          value: overAllAvailableLeave[category.leave_category_id] || 0,
          carryforward:
            overAllCarryforwardLeave[category.leave_category_id] || 0,
        },
        {
          type: "consumed",
          value: overAllConsumedLeave[category.leave_category_id] || 0,
        },
      ],
    }));

    setLeaveSummary(updatedLeaveData);
    setUserLeaves(updatedLeaveData);
  }, [leaveCatgeory, userList, leaveCategoryList, allLeaveConsumed]);

  useEffect(() => {
    let departmentStatistics = {};
    AllDepartmentList?.forEach((dept) => {
      departmentStatistics[dept.department_id] = {
        available: 0,
        consumed: 0,
        carryforward: 0,
        items: [],
      };
    });

    totalDepartmentLeave?.forEach((item) => {
      const deptId = Number(item.basedepartmentid);
      if (departmentStatistics[deptId]) {
        departmentStatistics[deptId].items.push(item);
        departmentStatistics[deptId].available += Number(item.available || 0);
        departmentStatistics[deptId].consumed += Number(item.consumed || 0);
        departmentStatistics[deptId].carryforward += Number(
          item.carry_forward || 0
        );
      }
    });

    const updatedData = AllDepartmentList.map((dept) => ({
      categoryName: dept.department_name,
      categoryid: dept.department_id,
      items: departmentStatistics[dept.department_id]?.items,
      data: [
        {
          type: "available",
          value: departmentStatistics[dept.department_id]?.available || 0,
          carryforward:
            departmentStatistics[dept.department_id]?.carryforward || 0,
        },
        {
          type: "consumed",
          value: departmentStatistics[dept.department_id]?.consumed || 0,
        },
      ],
    }));

    setAvailableLeaves(updatedData);
  }, [AllDepartmentList, totalDepartmentLeave]);

  const openDetailWindow = (name, id) => {
    setSelectedLeaveTypeId(id);
    setSelectedLeaveType(`Department's ${name} Balance`);
    setTtlDepartmentLeave(leavedataBasedonLeaveId[id]?.available);

    toggleStaffDepartmentLeaveModal(true);
  };

  const handlePreviewClick = async (data, type) => {
    try {
      const ext = data?.fileName?.split(".")?.pop();
      setLoading(true);
      const { code, url } = await CALL_API(
        `employeerequest/file?userId=${data?.userId}&requestId=${data?.requestId}&requestType=${type}`,
        "get",
        {}
      );
      if (code === STATUS_CODE.SUCCESS) {
        if (["jpg", "jpeg", "png", "svg", "pdf"].includes(ext)) {
          setPreviewURL(url);
          setPreview(true);
        } else {
          download(url);
        }
      } else {
        message.error(t("Preview failed. Try again."));
      }
    } catch {
      message.error(t("Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  const approveRejectCancelLeave = async (
    statusId,
    declined_reason = "",
    is_leave_cancel_request
  ) => {
    const payload = {
      userid: leaveDetails?.userid,
      departmentId: selectedDepartmentId,
      user_leave_id: leaveID,
      status_id: statusId.toString(),
      declined_reason,
      is_cancel_request: is_leave_cancel_request,
      start_date: leaveDetails?.new_start_date
        ? getMySqlDate(leaveDetails?.new_start_date)
        : getMySqlDate(leaveDetails?.start_date),
      end_date: leaveDetails?.new_end_date
        ? getMySqlDate(leaveDetails?.new_end_date)
        : getMySqlDate(leaveDetails?.end_date),
    };
    const { code } = await CALL_API(
      `approve-reject-cancel-leave/${ClientId}`,
      "post",
      {
        userid: leaveDetails?.userid,
        departmentId: selectedDepartmentId,
        user_leave_id: leaveID,
        status_id: statusId.toString(),
        declined_reason,
        is_cancel_request: is_leave_cancel_request,
        start_date: leaveDetails?.new_start_date
          ? getMySqlDate(leaveDetails?.new_start_date)
          : getMySqlDate(leaveDetails?.start_date),
        end_date: leaveDetails?.new_end_date
          ? getMySqlDate(leaveDetails?.new_end_date)
          : getMySqlDate(leaveDetails?.end_date),
      }
    );
    if (code === STATUS_CODE.SUCCESS) {
      setLoading(false);
      setIsModalOpen(false);
      updateSuccessModal({
        visibility: true,
        message: "Approved",
        Sucessicon: (
          <div className="d-flex justify-content-center align-items-center">
            <CheckCircleFilled style={{ color: "green" }} />
          </div>
        ),
      });
      getLeaveList();
      // Call webhook API
    } else {
      message.error(t("Something went wrong. Please try again."));
    }
  };

  const handleOk = () => {
    setLoading(true);
    approveRejectCancelLeave(5, "", havingCancel);
  };

  const handleFinish = async (status, value) => {
    try {
      setUpdateLoading(true);
      let payload = {};
      let requestType = "";

      if (tabId === "3") {
        requestType = "wfhrequest";
        payload = {
          wfhStartDate: dayjs(viewData?.wfhStartDate).format("YYYY-MM-DD"),
          wfhEndDate: dayjs(viewData?.wfhEndDate).format("YYYY-MM-DD"),
          wfhNoofDays: String(viewData?.wfhNoofDays),
          wfhReason: viewData?.wfhReason,
          reportingManager: viewData?.reportingManager,
          objectKey: viewData?.objectKey || "",
          fileName: viewData?.fileName || "",
          statusId: status,
          rejectReason: value?.decline_reason || "",
          requestType,
          approved_rejected_byName: userInfo?.name,
        };
      } else if (tabId === "4") {
        requestType = "permissionrequest";
        payload = {
          permissionDate: viewData?.permissionDate
            ? dayjs(viewData.permissionDate).format("YYYY-MM-DD")
            : "",
          permissionTime: viewData?.permissionTime,
          compensatingDate: viewData?.compensatingDate,
          permissionReason: viewData?.permissionReason,
          reportingManager: viewData?.reportingManager,
          objectKey: viewData?.objectKey || "",
          fileName: viewData?.fileName || "",
          statusId: status,
          rejectReason: value?.decline_reason || "",
          requestType,
          approved_rejected_byName: userInfo?.name,
        };
      } else {
        requestType = "overtimerequest";
        payload = {
          overtimeDate: viewData?.overtimeDate
            ? dayjs(viewData.overtimeDate).format("YYYY-MM-DD")
            : "",
          overtimeTime: viewData?.overtimeTime,
          overtimeReason: viewData?.overtimeReason,
          reportingManager: viewData?.reportingManager,
          objectKey: viewData?.objectKey || "",
          fileName: viewData?.fileName || "",
          statusId: status,
          rejectReason: value?.decline_reason || "",
          requestType,
          approved_rejected_byName: userInfo?.name,
        };
      }

      const { code } = await CALL_API(
        `employeerequest/${viewData?.userId}/${viewData?.requestId}?requestType=${requestType}`,
        "patch",
        payload
      );

      if (code === STATUS_CODE.SUCCESS) {
        setPopupOpen(true);
        setOpen(false);
        setViewData({});
        setIsOpenModal(false);
        setTimeout(() => {
          setPopupOpen(false);
          updateform.resetFields();
          if (tabId === "2") getOvertimeData();
          else if (tabId === "3") getWfhData();
          else if (tabId === "4") getTimeOffData();
        }, 2000);
      }
    } catch (err) {
      console.error(err);
      message.error(t("Something went wrong"));
    } finally {
      setUpdateLoading(false);
    }
  };

  const prepareWfhCSV = () => {
    if (wfhData?.length > 0) {
      const csvData = wfhData.map((item) => {
        const user = userList?.find((val) => val?.userid === item?.userId);
        const base = {
          Name: user?.fullname || "-",
          "Employee ID": user?.employeeid || "-",
          "Start Date": item?.wfhStartDate
            ? dayjs(item.wfhStartDate).format("Do MMM YYYY - ddd")
            : "-",
          "End Date": item?.wfhEndDate
            ? dayjs(item.wfhEndDate).format("Do MMM YYYY - ddd")
            : "-",
          "Total Days": item?.wfhNoofDays || "-",
          "Applied on": item?.createdAt
            ? dayjs(item.createdAt).format("Do MMM YYYY - ddd")
            : "-",
          "Reason for WFH": item?.wfhReason || "-",
        };
        if (selectedStatus === "1")
          base["Approved by"] = item?.approved_rejected_byName || "-";
        if (selectedStatus === "2") {
          base["Rejected by"] = item?.approved_rejected_byName || "-";
          base["Decline Reason"] = item?.rejectReason || "-";
        }
        return base;
      });
      setCSVDataList(csvData);
      setTimeout(() => csvLinkEl.current.link.click());
    } else {
      message.error(t("No data available to download."));
    }
  };

  const prepareOvertimeCSV = () => {
    if (overtimeData?.length > 0) {
      const csvData = overtimeData.map((item) => {
        const user = userList?.find((val) => val?.userid === item?.userId);
        const base = {
          Name: user?.fullname || "-",
          "Employee ID": user?.employeeid || "-",
          "Date - Day": item?.overtimeDate
            ? dayjs(item.overtimeDate).format("Do MMM YYYY - ddd")
            : "-",
          Time: item?.overtimeTime
            ? dayjs(item.overtimeTime).format("HH [hrs] mm [mins]")
            : "-",
          "Applied on": item?.createdAt
            ? dayjs(item.createdAt).format("Do MMM YYYY - ddd")
            : "-",
          "Reason for Overtime": item?.overtimeReason || "-",
        };
        if (selectedStatus === "1")
          base["Approved by"] = item?.approved_rejected_byName || "-";
        if (selectedStatus === "2") {
          base["Rejected by"] = item?.approved_rejected_byName || "-";
          base["Decline Reason"] = item?.rejectReason || "-";
        }
        return base;
      });
      setCSVDataList(csvData);
      setTimeout(() => csvLinkEl.current.link.click());
    } else {
      message.error(t("No data available to download."));
    }
  };

  const prepareTimeOffCSV = () => {
    if (timeOffData?.length > 0) {
      const csvData = timeOffData.map((item) => {
        const user = userList?.find((val) => val?.userid === item?.userId);
        const base = {
          Name: user?.fullname || "-",
          "Employee ID": user?.employeeid || "-",
          "Date - Day": item?.permissionDate
            ? dayjs(item.permissionDate).format("Do MMM YYYY - ddd")
            : "-",
          Time: item?.permissionTime
            ? dayjs(item.permissionTime).format("HH [hrs] mm [mins]")
            : "-",
          "Day to Compensate": item?.compensatingDate
            ? dayjs(item.compensatingDate).format("Do MMM YYYY - ddd")
            : "-",
          "Applied on": item?.createdAt
            ? dayjs(item.createdAt).format("Do MMM YYYY - ddd")
            : "-",
          "Reason for Time Off in Lieu": item?.permissionReason || "-",
        };
        if (selectedStatus === "1")
          base["Approved by"] = item?.approved_rejected_byName || "-";
        if (selectedStatus === "2") {
          base["Rejected by"] = item?.approved_rejected_byName || "-";
          base["Decline Reason"] = item?.rejectReason || "-";
        }
        return base;
      });
      setCSVDataList(csvData);
      setTimeout(() => csvLinkEl.current.link.click());
    } else {
      message.error(t("No data available to download."));
    }
  };

  const prepareCSV = () => {
    if (userLeaves.length > 0) {
      setCSVDepartmentLeave(
        reduce(
          userLeaves.filter((value) => {
            const available =
              value.data.find((i) => i.type === "available")?.value || 0;
            const consumed =
              value.data.find((i) => i.type === "consumed")?.value || 0;
            return available !== 0 || consumed !== 0;
          }),
          (result, leave) => {
            result.push({
              "Leave Category ID": leave.id,
              "Leave Category Name": leave.categoryName,
              "Available Leaves": leave.data[0].value,
              "Consumed Leaves": leave.data[1].value,
              CF: leave.data[0].carryforward,
            });
            return result;
          },
          []
        )
      );
      setTimeout(() => csvLinkDepartment.current.link.click());
    }
  };

  const leaveColumns = [
    { title: t("Name"), dataIndex: "name", key: "name", width: "10%" },
    {
      title: t("Employee ID"),
      dataIndex: "employeeid",
      key: "employeeid",
      width: "7%",
    },
    {
      title: t("Department"),
      dataIndex: "department_name",
      key: "department_name",
      width: "10%",
    },
    {
      title: t("Start Date - Day"),
      dataIndex: "start_date",
      key: "start_date",
      width: "10%",
      render: (_, record) => {
        const date =
          !record.new_start_date || record.new_start_date === "null"
            ? record.start_date
            : record.new_start_date;
        return `${moment(date).format("DD MMM YYYY")} - ${moment(date).format(
          "dddd"
        )}`;
      },
    },
    {
      title: t("End Date - Day"),
      dataIndex: "end_date",
      key: "end_date",
      width: "10%",
      render: (_, record) => {
        const date =
          !record.new_end_date || record.new_end_date === "null"
            ? record.end_date
            : record.new_end_date;
        return `${moment(date).format("DD MMM YYYY")} - ${moment(date).format(
          "dddd"
        )}`;
      },
    },
    {
      title: t("Days"),
      dataIndex: "number_of_days",
      key: "number_of_days",
      width: "7%",
      render: (_, record) =>
        !record.new_number_of_days || record.new_number_of_days === "null"
          ? record.number_of_days
          : record.new_number_of_days,
    },
    {
      title: t("Leave Type"),
      dataIndex: "leave_category_name",
      key: "leave_category_name",
      width: "10%",
    },
    {
      title: t("Reliever Name"),
      dataIndex: "relievername",
      key: "relievername",
      width: "10%",
      render: (text) => {
        return text ? text : "-";
      },
    },
    {
      title: t("Amended Leave"),
      dataIndex: "having_cancel_request",
      key: "having_cancel_request",
      width: "10%",
      render: (val) => (val === "1" ? t("Yes") : t("No")),
    },
    {
      title: t("Applied On"),
      dataIndex: "created_at",
      key: "created_at",
      width: "10%",
      render: (_, record) => moment(record.created_at).format("DD MMM YYYY"),
    },
  ];

  const overtimeColumns = [
    {
      title: t("Name"),
      dataIndex: "userId",
      key: "overtime_name",
      render: (text) => users?.find((val) => val?.userid === text)?.fullname,
    },
    {
      title: t("Employee ID"),
      dataIndex: "userId",
      key: "overtime_empid",
      render: (text) => users?.find((val) => val?.userid === text)?.employeeid,
    },
    {
      title: t("Date - Day"),
      dataIndex: "overtimeDate",
      key: "overtimeDate",
      width: 150,
      render: (date) => dayjs(date)?.format("Do MMM YYYY - ddd"),
    },
    {
      title: t("Time"),
      dataIndex: "overtimeTime",
      key: "overtimeTime",
      width: 150,
      render: (date) => dayjs(date)?.format("HH [hrs] mm [mins]"),
    },
    {
      title: t("Attachment"),
      dataIndex: "fileName",
      key: "overtime_file",
      width: 150,
      render: (text, record) =>
        text ? (
          <a
            href="#"
            style={{ color: "#2d53da" }}
            onClick={(e) => {
              e.stopPropagation();
              handlePreviewClick(record, "overtimerequest");
            }}
          >
            {text}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: t("Applied on"),
      dataIndex: "createdAt",
      key: "overtime_createdAt",
      width: 150,
      render: (date) => dayjs(date)?.format("Do MMM YYYY - ddd"),
    },
    {
      title: t("Reason for Overtime"),
      dataIndex: "overtimeReason",
      key: "overtimeReason",
      width: 150,
      className: "reason-column",
    },
  ];

  const wfhColumns = [
    {
      title: t("Name"),
      dataIndex: "userId",
      key: "wfh_name",
      render: (text) => users?.find((val) => val?.userid === text)?.fullname,
    },
    {
      title: t("Employee ID"),
      dataIndex: "userId",
      key: "wfh_empid",
      render: (text) => users?.find((val) => val?.userid === text)?.employeeid,
    },
    {
      title: t("Start Date"),
      dataIndex: "wfhStartDate",
      key: "wfhStartDate",
      render: (date) => dayjs(date)?.format("Do MMM YYYY - ddd"),
    },
    {
      title: t("End Date"),
      dataIndex: "wfhEndDate",
      key: "wfhEndDate",
      render: (date) => dayjs(date)?.format("Do MMM YYYY - ddd"),
    },
    { title: t("Total Days"), dataIndex: "wfhNoofDays", key: "wfhNoofDays" },
    {
      title: t("Attachment"),
      dataIndex: "fileName",
      key: "wfh_file",
      render: (text, record) =>
        text ? (
          <a
            href="#"
            style={{ color: "#2d53da" }}
            onClick={(e) => {
              e.stopPropagation();
              handlePreviewClick(record, "wfhrequest");
            }}
          >
            {text}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: t("Applied on"),
      dataIndex: "createdAt",
      key: "wfh_createdAt",
      render: (date) => dayjs(date)?.format("Do MMM YYYY - ddd"),
    },
    {
      title: t("Reason for WFH"),
      dataIndex: "wfhReason",
      key: "wfhReason",
      className: "reason-column",
    },
  ];

  const timeOffColumns = [
    {
      title: t("Name"),
      dataIndex: "userId",
      key: "timeoff_name",
      render: (text) => users?.find((val) => val?.userid === text)?.fullname,
    },
    {
      title: t("Employee ID"),
      dataIndex: "userId",
      key: "timeoff_empid",
      render: (text) => users?.find((val) => val?.userid === text)?.employeeid,
    },
    {
      title: t("Date - Day"),
      dataIndex: "permissionDate",
      key: "permissionDate",
      render: (date) => dayjs(date)?.format("Do MMM YYYY - ddd"),
    },
    {
      title: t("Time"),
      dataIndex: "permissionTime",
      key: "permissionTime",
      render: (date) => dayjs(date)?.format("HH [hrs] mm [mins]"),
    },
    {
      title: t("Day to Compensate"),
      dataIndex: "compensatingDate",
      key: "compensatingDate",
      render: (date) => dayjs(date)?.format("Do MMM YYYY - ddd"),
    },
    {
      title: t("Attachment"),
      dataIndex: "fileName",
      key: "timeoff_file",
      render: (text, record) =>
        text ? (
          <a
            href="#"
            style={{ color: "#2d53da" }}
            onClick={(e) => {
              e.stopPropagation();
              handlePreviewClick(record, "permissionrequest");
            }}
          >
            {text}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: t("Applied on"),
      dataIndex: "createdAt",
      key: "timeoff_createdAt",
      render: (date) => dayjs(date)?.format("Do MMM YYYY - ddd"),
    },
    {
      title: t("Reason for Time Off in Lieu"),
      dataIndex: "permissionReason",
      key: "permissionReason",
      className: "reason-column",
    },
  ];

  const departmentOptions = concat(
    orderBy(AllDepartmentList, [(val) => val.department_id], ["asc"]).map(
      (dept) => ({ value: dept.department_id, label: dept.department_name })
    )
  );

  const filterleavereport = selectedDepartment
    ? leaveList.filter(
        (item) =>
          item.status_id === "4" && item.department_id == selectedDepartment
      )
    : leaveList;

  const statusLabel =
    selectedStatus === "0"
      ? t("Waiting for Approval")
      : selectedStatus === "1"
      ? t("Approved")
      : t("Rejected");

  const storeUserData = [];

  const tabItems = [
    { key: "1", label: t("Leave") },
    { key: "2", label: t("Overtime") },
    { key: "3", label: t("WFH") },
    { key: "4", label: t("Time Off in Lieu") },
  ];

  const statusOptions = [
    { label: t("Waiting For Approval"), value: "0" },
    { label: t("Approved"), value: "1" },
    { label: t("Rejected"), value: "2" },
  ];

  return (
    <>
      <StyledRow gutter={16}>
        <Col xs={24}>
          <Col xs={24} className="text-end mb-2">
            <DatePicker
              format="DD-MM-YYYY"
              value={filterdate}
              disabledDate={(current) =>
                current && current > dayjs().endOf("day")
              }
              onChange={(date) => setfilterdate(date)}
            />
          </Col>
        </Col>
        <Col md={24} sm={24} xs={24} xl={12}>
          <AttendanceBarChart isHrDashboard={true} filterdate={filterdate} />
        </Col>
        <Col md={24} sm={24} xs={24} xl={12}>
          <LocationBasedPresentStaff
            isHrDashboard={true}
            filterdate={filterdate}
          />
        </Col>

        <Col md={24} sm={24} xs={24} xl={24} ref={containerRef}>
          <StyledChartContainer>
            <Tabs
              defaultActiveKey={tabId}
              items={tabItems}
              onChange={(key) => {
                setTabId(key);
                setSelectedStatus("0");
              }}
            />

            {tabId === "1" &&
              Menulist?.find(
                (item) =>
                  item?.key === "allapprovals" &&
                  item?.subMenu?.find(
                    (subItem) =>
                      subItem?.key === "approvals" && subItem?.permission?.view
                  )
              ) && (
                <>
                  <div className="chartstyle">
                    <StyledTitle>
                      {t("Waiting for Approval")} ({filterleavereport?.length})
                    </StyledTitle>
                    <StyledForm
                      form={form}
                      name="approval_form"
                      layout="inline"
                    >
                      <Select
                        className="select"
                        placeholder={
                          <>
                            <img src={Filter} alt="filter" />{" "}
                            <span>{t("Department")}</span>
                          </>
                        }
                        options={departmentOptions}
                        onChange={setSelectedDepartment}
                        value={selectedDepartment}
                        getPopupContainer={() => containerRef.current}
                      />
                    </StyledForm>
                  </div>
                  <Spin spinning={displayTaskLoader}>
                    <StyledTable
                      dataSource={filterleavereport}
                      columns={leaveColumns}
                      rowClassName={(_, index) =>
                        index % 2 === 0 ? "" : "non-white"
                      }
                      onRow={(record) => ({
                        onClick: () => {
                          if (
                            Menulist?.find(
                              (item) =>
                                item?.key === "allapprovals" &&
                                item?.subMenu?.find(
                                  (subItem) =>
                                    subItem?.key === "approvals" &&
                                    subItem?.permission?.edit
                                )
                            )
                          ) {
                            toggleDisplayLeaveModal(true);
                            setLeaveDetails({ ...record, isViewOnly: true });
                          }
                        },
                      })}
                      rowKey="user_leave_id"
                      pagination={
                        filterleavereport.length > 10
                          ? {
                              showTotal: (total, range) =>
                                `${range[0]}-${range[1]} ${t(
                                  "of"
                                )} ${total} ${t("items")}`,
                              pageSize: 10,
                              showSizeChanger: false,
                            }
                          : false
                      }
                    />
                  </Spin>
                </>
              )}

            {tabId === "2" && (
              <>
                <div className="d-flex">
                  <StyledTitle>
                    {statusLabel} ({overtimeData?.length})
                  </StyledTitle>
                  <div>
                    <Select
                      placeholder="Status"
                      style={{ width: 200, margin: "0px 10px" }}
                      options={statusOptions}
                      getPopupContainer={() => containerRef.current}
                      onChange={setSelectedStatus}
                      value={selectedStatus}
                    />

                    <ButtonOutlined
                      onClick={prepareOvertimeCSV}
                      disabled={overtimeData.length === 0}
                    >
                      {t("Export CSV file")}
                    </ButtonOutlined>
                    <CSVLink
                      data={CSVDataList}
                      filename="overtime.csv"
                      asyncOnClick
                      ref={csvLinkEl}
                    />
                  </div>
                </div>
                <Spin spinning={loading}>
                  <StyledTable
                    columns={overtimeColumns}
                    dataSource={overtimeData}
                    rowKey="requestId"
                    pagination={
                      overtimeData?.length > 10
                        ? { pageSize: 10, showSizeChanger: false }
                        : false
                    }
                    rowClassName={(_, index) => (index % 2 ? "non-white" : "")}
                    onRow={(record) => ({
                      onClick: () => {
                        if (selectedStatus === "0") {
                          setOpen(true);
                          setViewData(record);
                        }
                      },
                    })}
                  />
                </Spin>
              </>
            )}

            {tabId === "3" && (
              <>
                <div className="d-flex">
                  <StyledTitle>
                    {statusLabel} ({wfhData?.length})
                  </StyledTitle>
                  <div>
                    <Select
                      placeholder="Status"
                      style={{ width: 200, margin: "0px 10px" }}
                      options={statusOptions}
                      getPopupContainer={() => containerRef.current}
                      onChange={setSelectedStatus}
                      value={selectedStatus}
                    />
                    <ButtonOutlined
                      onClick={prepareWfhCSV}
                      disabled={wfhData.length === 0}
                    >
                      {t("Export CSV file")}
                    </ButtonOutlined>
                    <CSVLink
                      data={CSVDataList}
                      filename="WFH.csv"
                      asyncOnClick
                      ref={csvLinkEl}
                    />
                  </div>
                </div>
                <Spin spinning={loading}>
                  <StyledTable
                    columns={wfhColumns}
                    dataSource={wfhData}
                    rowKey="requestId"
                    pagination={
                      wfhData?.length > 10
                        ? { pageSize: 10, showSizeChanger: false }
                        : false
                    }
                    rowClassName={(_, index) => (index % 2 ? "non-white" : "")}
                    onRow={(record) => ({
                      onClick: () => {
                        if (selectedStatus === "0") {
                          setOpen(true);
                          setViewData(record);
                        }
                      },
                    })}
                  />
                </Spin>
              </>
            )}

            {tabId === "4" && (
              <>
                <div className="d-flex">
                  <StyledTitle>
                    {statusLabel} ({timeOffData?.length})
                  </StyledTitle>
                  <div>
                    <Select
                      placeholder="Status"
                      style={{ width: 200, margin: "0px 10px" }}
                      options={statusOptions}
                      getPopupContainer={() => containerRef.current}
                      onChange={setSelectedStatus}
                      value={selectedStatus}
                    />
                    <ButtonOutlined
                      onClick={prepareTimeOffCSV}
                      disabled={timeOffData.length === 0}
                    >
                      {t("Export CSV file")}
                    </ButtonOutlined>
                    <CSVLink
                      data={CSVDataList}
                      filename="timeoff in lieu.csv"
                      asyncOnClick
                      ref={csvLinkEl}
                    />
                  </div>
                </div>
                <Spin spinning={loading}>
                  <StyledTable
                    columns={timeOffColumns}
                    dataSource={timeOffData}
                    rowKey="requestId"
                    pagination={
                      timeOffData?.length > 10
                        ? { pageSize: 10, showSizeChanger: false }
                        : false
                    }
                    rowClassName={(_, index) => (index % 2 ? "non-white" : "")}
                    onRow={(record) => ({
                      onClick: () => {
                        if (selectedStatus === "0") {
                          setOpen(true);
                          setViewData(record);
                        }
                      },
                    })}
                  />
                </Spin>
              </>
            )}

            <LeaveFormModel
              isModalVisible={displayLeaveModal}
              closeModal={() => toggleDisplayLeaveModal(false)}
              userRoleId={userRoleId}
              leaveDetails={leaveDetails}
              toggleDisplayLeaveModal={toggleDisplayLeaveModal}
              getLeaveList={getLeaveList}
              setIsModalOpen={setIsModalOpen}
              setLeaveID={setLeaveID}
              sethavingCancel={sethavingCancel}
              ClientId={ClientId}
            />
          </StyledChartContainer>
        </Col>

        <Col md={24} sm={24} xs={24} xl={24} className="mt-4">
          <Spin spinning={displayLeaveReportLoader}>
            <StyledLeaveRow gutter={24}>
              <Col span={12}>
                <StyledTitle>
                  {t("Overall Department's Leave Report")}
                </StyledTitle>
              </Col>
              <Col
                span={12}
                className="leavereportstyle"
                style={{ justifyContent: "flex-end", display: "flex" }}
              >
                <ButtonOutlined
                  onClick={prepareCSV}
                  disabled={userLeaves.length === 0}
                >
                  {t("Export CSV file")}
                </ButtonOutlined>
                <CSVLink
                  header={[
                    { label: t("Employee ID") },
                    { label: t("Name") },
                    { label: t("Available Leaves") },
                    { label: t("Consumed Leaves") },
                    { label: t("Total Leaves") },
                  ]}
                  data={CSVDepartmentLeave}
                  filename="Overall Leave Report.csv"
                  asyncOnClick
                  ref={csvLinkDepartment}
                />
              </Col>
              {Array.isArray(leaveSummary) &&
                leaveSummary
                  .filter((value) => {
                    const available =
                      value.data.find((i) => i.type === "available")?.value ||
                      0;
                    const consumed =
                      value.data.find((i) => i.type === "consumed")?.value || 0;
                    return available !== 0 || consumed !== 0;
                  })
                  .map((value) => (
                    <Col
                      md={24}
                      sm={24}
                      xs={24}
                      xl={12}
                      key={value.categoryName}
                      onClick={() =>
                        openDetailWindow(value.categoryName, value.id)
                      }
                      className="mt-2"
                    >
                      <LeaveDonutChart
                        iconName={LEAVE_ICON[7]}
                        title={`${
                          value.categoryName
                        } - ${new Date().getFullYear()}`}
                        data={value.data}
                        leaveId={value?.id}
                      />
                    </Col>
                  ))}
            </StyledLeaveRow>
          </Spin>
        </Col>
      </StyledRow>

      <ModalWrap
        title=""
        visible={isOpenModal}
        centered
        keyboard={false}
        maskClosable={false}
        width={400}
        footer={[]}
        onCancel={() => setIsOpenModal(false)}
      >
        <Form
          form={updateform}
          name="reasonform"
          layout="vertical"
          initialValues={{ reason: "" }}
          onFinish={(values) => handleFinish("2", values)}
        >
          <Form.Item
            label={t("Reason for Rejection")}
            name="decline_reason"
            rules={[
              {
                required: true,
                pattern: NON_EMPTY_REGEX,
                message: t("Please enter reject reason!"),
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder={t("Type something..")}
              maxLength={500}
            />
          </Form.Item>
          <div className="align-right">
            <ButtonOutlined onClick={() => setIsOpenModal(false)}>
              {t("Cancel")}
            </ButtonOutlined>
            <ButtonFilled
              htmlType="submit"
              type="primary"
              loading={updateLoading}
            >
              {t("Decline")}
            </ButtonFilled>
          </div>
        </Form>
      </ModalWrap>

      <Modal
        title={t("Approve leave")}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="back" onClick={() => setIsModalOpen(false)}>
            {t("No")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleOk}
          >
            {t("Yes")}
          </Button>,
        ]}
      >
        <p>{t("Are you sure you want to approve leave?")}</p>
      </Modal>

      <AllStaffLeaveBalance
        displayModal={staffDepartmentLeaveModal}
        toggleModal={() => toggleStaffDepartmentLeaveModal(false)}
        leaveTypeId={selectedLeaveTypeId}
        selectedDepartmentId={selectedDepartmentId}
        leaveName={selectedLeaveType}
        leaveList={leaveList}
        leaveCategoryList={leaveCategoryList}
        leaveCatgeory={leaveCatgeory}
        all={true}
        storeUserData={storeUserData}
        availableLeaves={availableLeaves}
      />

      <ModalWrap
        open={open}
        onCancel={() => {
          setOpen(false);
          setViewData({});
        }}
        footer={null}
        centered
        width={520}
        closable={false}
        title={
          tabId === "2"
            ? t("Overtime Request")
            : tabId === "3"
            ? t("WFH Request")
            : t("Time Off in Lieu Request")
        }
      >
        <CloseOutlined
          className="close-icon"
          onClick={() => {
            setOpen(false);
            setViewData({});
          }}
        />

        <div className="applied-tag">
          {t("Applied on")}&nbsp;&nbsp;
          <b className="value">
            {dayjs(viewData?.createdAt).format("Do MMM, YYYY")}
          </b>
        </div>

        {tabId === "3" && (
          <>
            <Row gutter={[8, 14]}>
              <Col xs={24} sm={12}>
                <div className="label">{t("Start Date")}</div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="value">
                  {dayjs(viewData?.wfhStartDate)?.format("Do MMM YYYY - ddd")}
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="label">{t("End Date")}</div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="value">
                  {dayjs(viewData?.wfhEndDate)?.format("Do MMM YYYY - ddd")}
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="label">{t("Total Days")}</div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="value">{viewData?.wfhNoofDays}</div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="label">{t("Attachment")}</div>
              </Col>
              <Col xs={24} sm={12}>
                <div
                  className="value attachment"
                  onClick={() => {
                    setOpen(false);
                    handlePreviewClick(viewData, "wfhrequest");
                  }}
                >
                  {viewData?.fileName || "-"}
                </div>
              </Col>
            </Row>
            <div className="reason-title">{t("Reason for WFH")}</div>
            <div className="reason-text">{viewData?.wfhReason}</div>
          </>
        )}

        {tabId === "4" && (
          <>
            <Row gutter={[8, 14]}>
              <Col xs={24} sm={12}>
                <div className="label">{t("Date")}</div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="value">
                  {dayjs(viewData?.permissionDate)?.format("Do MMM YYYY - ddd")}
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="label">{t("Time")}</div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="value">
                  {dayjs(viewData?.permissionTime)?.format(
                    "HH [hrs] mm [mins]"
                  )}
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="label">{t("Day to Compensate")}</div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="value">
                  {dayjs(viewData?.compensatingDate)?.format(
                    "Do MMM YYYY - ddd"
                  )}
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="label">{t("Attachment")}</div>
              </Col>
              <Col xs={24} sm={12}>
                <div
                  className="value attachment"
                  onClick={() => {
                    setOpen(false);
                    handlePreviewClick(viewData, "permissionrequest");
                  }}
                >
                  {viewData?.fileName || "-"}
                </div>
              </Col>
            </Row>
            <div className="reason-title">
              {t("Reason for Time Off in Lieu")}
            </div>
            <div className="reason-text">{viewData?.permissionReason}</div>
          </>
        )}

        {tabId === "2" && (
          <>
            <Row gutter={[8, 14]}>
              <Col xs={24} sm={12}>
                <div className="label">{t("Date")}</div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="value">
                  {dayjs(viewData?.overtimeDate)?.format("Do MMM YYYY - ddd")}
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="label">{t("Time")}</div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="value">
                  {dayjs(viewData?.overtimeTime)?.format("HH [hrs] mm [mins]")}
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="label">{t("Attachment")}</div>
              </Col>
              <Col xs={24} sm={12}>
                <div
                  className="value attachment"
                  onClick={() => {
                    setOpen(false);
                    handlePreviewClick(viewData, "overtimerequest");
                  }}
                >
                  {viewData?.fileName || "-"}
                </div>
              </Col>
            </Row>
            <div className="reason-title">{t("Reason for Overtime")}</div>
            <div className="reason-text">{viewData?.overtimeReason}</div>
          </>
        )}

        <div className="w-100 text-end mt-3">
          <ButtonOutlined
            onClick={() => {
              setOpen(false);
              setIsOpenModal(true);
            }}
          >
            {t("Decline")}
          </ButtonOutlined>
          <ButtonFilled
            onClick={() => handleFinish("1")}
            loading={updateLoading}
          >
            {t("Approve")}
          </ButtonFilled>
        </div>
      </ModalWrap>

      <ViewDocument
        open={preview}
        onCancel={() => {
          setPreview(false);
          setPreviewURL("");
        }}
        data={previewURL}
      />
      <SuccessDeclinePopup
        open={popupOpen}
        setOpen={setPopupOpen}
        type={
          updateform?.getFieldValue("decline_reason") ? "decline" : "success"
        }
        message={
          updateform?.getFieldValue("decline_reason") ? "Declined" : "Approved"
        }
      />
    </>
  );
}

export default HrDashboard;
